// src/MarketingLeadsVsBookings.js
import React, { useState } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Grid,
  CircularProgress,
} from "@mui/material";

const MarketingLeadsVsBookings = () => {
  // Get the current date for default values
  const getCurrentDate = () => {
    const now = new Date();
    // Format as YYYY-MM-DD
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);


  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    
    // Parse the date without timezone issues
    // Split the date string into components
    const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
    
    // Create a date object with the correct values
    // Months are 0-indexed in JavaScript Date objects
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleRunReport = () => {
    if (!startDate || !endDate) {
      setError("Please select start and end dates.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    // Call the API endpoint
    apiFetch(`/api/reports/marketing-leads-vs-bookings?start_date=${startDate}&end_date=${endDate}`)
      .then((res) => {
        if (!res.ok) {
          return res.text().then(text => { 
            throw new Error("Failed to fetch report data: " + text);
          });
        }
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportGeneratedAt(new Date());
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error running report:", err);
        setError("Error running report: " + err.message);
        setLoading(false);
      });
  };

  // Calculate totals for the summary (or use the TOTALS row from the server)
  const calculateTotals = () => {
    if (!reportData || reportData.length === 0) return null;
    
    // Check if the server already provided a TOTALS row
    const totalsRow = reportData.find(row => row.service_area === 'GRAND TOTALS');
    if (totalsRow) {
      return {
        totalAllAppts: totalsRow.total_appointments || 0,
        totalAppts: totalsRow.fb_appointments || 0,
        totalMessages: totalsRow.fb_messages || 0,
        totalBudget: totalsRow.fb_ad_budget || 0,
        totalSpend: totalsRow.fb_total_spend || 0
      };
    }
    
    // If no TOTALS row, calculate it manually
    return reportData.reduce((totals, item) => {
      return {
        totalAllAppts: totals.totalAllAppts + (item.total_appointments || 0),
        totalAppts: totals.totalAppts + (item.fb_appointments || 0),
        totalMessages: totals.totalMessages + (item.fb_messages || 0),
        totalBudget: totals.totalBudget + (item.fb_ad_budget || 0),
        totalSpend: totals.totalSpend + (item.fb_total_spend || 0)
      };
    }, { totalAllAppts: 0, totalAppts: 0, totalMessages: 0, totalBudget: 0, totalSpend: 0 });
  };

  const totals = calculateTotals();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Marketing Leads vs Bookings Report
      </Typography>
      
      {/* Report criteria form */}
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 600, mb: 2 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder={getCurrentDate()}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder={getCurrentDate()}
            />
          </Grid>
          <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button 
              variant="contained" 
              onClick={handleRunReport}
              disabled={loading}
              fullWidth
            >
              {loading ? "Loading..." : "Run Report"}
            </Button>
          </Grid>
        </Grid>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Report header with title, explanation, and generation timestamp */}
      {reportGeneratedAt && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6">
              Marketing Leads vs Bookings: {formatDate(startDate)} to {formatDate(endDate)}
            </Typography>
            <Typography variant="caption">
              Report generated: {reportGeneratedAt.toLocaleString()}
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> This report shows both total appointments and those with Facebook referral sources.
              The difference between these columns indicates how many appointments are missing referral source data.
              Days with appointments but no marketing data may indicate missing marketing lead entries.
              <strong>Total Booked Appointments column only counts first time appointments. It does not includes repeat appointments.
              Therefore this columns total will not match the daily booking report.</strong>
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* Report results display */}
      {reportData && reportData.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {/* Summary section */}
          {totals && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Total Booked Appointments</TableCell>
                      <TableCell>Booked Appointments with Referral Source</TableCell>
                      <TableCell align="right">Total FB Messages</TableCell>
                      <TableCell align="right">Total Ad Budget</TableCell>
                      <TableCell align="right">Total Ad Spend</TableCell>
                      <TableCell align="right">Conversion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{totals.totalAllAppts}</TableCell>
                      <TableCell>{totals.totalAppts}</TableCell>
                      <TableCell align="right">{totals.totalMessages}</TableCell>
                      <TableCell align="right">{formatCurrency(totals.totalBudget)}</TableCell>
                      <TableCell align="right">{formatCurrency(totals.totalSpend)}</TableCell>
                      <TableCell align="right">
                        {totals.totalMessages > 0 
                          ? `${((totals.totalAppts / totals.totalMessages) * 100).toFixed(1)}%` 
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          {/* Detailed table by service area */}
          <Typography variant="h6" gutterBottom>Details by Service Area</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service Area</TableCell>
                  <TableCell align="right">Total Booked Appointments</TableCell>
                  <TableCell align="right">Booked Appointments with Referral Source</TableCell>
                  <TableCell align="right">Facebook Messages</TableCell>
                  <TableCell align="right">Ad Budget</TableCell>
                  <TableCell align="right">Avg Cost Per Lead</TableCell>
                  <TableCell align="right">Total Ad Spend</TableCell>
                  <TableCell align="right">Cost Per Appointment</TableCell>
                  <TableCell align="right">Conversion Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row, idx) => {
                  // Check if this is a new date (or the first row)
                  const isNewDate = idx === 0 || (row.date !== reportData[idx - 1].date && !row.date.includes('ALL'));
                  
                  // Determine styling based on whether it's a total row
                  const isTotal = row.is_total;
                  const isGrandTotal = row.service_area === 'GRAND TOTALS';
                  
                  const rowStyle = isGrandTotal
                    ? { fontWeight: 'bold', backgroundColor: '#e3f2fd' }
                    : isTotal
                      ? { fontWeight: 'bold', backgroundColor: '#f5f5f5' }
                      : {};
                      
                  const cellStyle = isGrandTotal || isTotal ? { fontWeight: 'bold' } : {};
                  
                  return (
                    <React.Fragment key={idx}>
                      {/* Add a date header row if this is a new date */}
                      {isNewDate && !isTotal && (
                        <TableRow 
                          sx={{ 
                            backgroundColor: '#f9f9f9',
                            borderTop: idx > 0 ? '2px solid #ddd' : 'none'
                          }}
                        >
                          <TableCell colSpan={9} sx={{ py: 1 }}>
                            <Typography variant="subtitle2">
                              <strong>Date:</strong> {formatDate(row.date)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      
                      <TableRow sx={rowStyle}>
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{
                            ...cellStyle,
                            paddingLeft: isTotal ? 2 : 4, // Indent non-total rows
                          }}
                        >
                          {isTotal ? row.service_area : row.service_area}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>{row.total_appointments || 0}</TableCell>
                        <TableCell align="right" sx={cellStyle}>{row.fb_appointments || 0}</TableCell>
                        <TableCell align="right" sx={cellStyle}>{row.fb_messages || 0}</TableCell>
                        <TableCell align="right" sx={cellStyle}>{formatCurrency(row.fb_ad_budget)}</TableCell>
                        <TableCell align="right" sx={cellStyle}>{formatCurrency(row.fb_avg_cost_per_lead)}</TableCell>
                        <TableCell align="right" sx={cellStyle}>{formatCurrency(row.fb_total_spend)}</TableCell>
                        <TableCell align="right" sx={cellStyle}>{formatCurrency(row.cost_per_appointment)}</TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {row.fb_messages > 0 
                            ? `${((row.fb_appointments / row.fb_messages) * 100).toFixed(1)}%` 
                            : 'N/A'}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </Box>
      ) : reportGeneratedAt && (
        <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
          No data found for the selected date range.
        </Typography>
      )}
    </Box>
  );
};
export default MarketingLeadsVsBookings;